import { NavigationItemType } from '@shared/enum/navigation-item-type';
import { DataHistory } from '@shared/models/data-model';
import { genericAppIndexId } from '@/app/egi.pages/app-root.pages/page-ids';
import { NavigationItemData } from '@/app/platform/platform-shared/models/navigation-item.data';

export class UserNavigationItem extends NavigationItemData {
  get isGenericAppIndex(): boolean {
    return this.pageComponentId === genericAppIndexId;
  }

  get isMenu(): boolean {
    return this.type === NavigationItemType.Menu;
  }

  get routerLink(): string | undefined {
    return this.egiPage ? `/${this.appId}/${this.egiPage.fullPathFromRoot}` : undefined;
  }

  /**
   * Indicates if clicking on the item opens a page or its just a menuitem that has children
   */
  get isDirectlyNavigable(): boolean {
    return (
      !UserNavigationItem.nonDirectlyNavigableTypes.includes(this.type) && this.isBaseAccessAllowed
    );
  }

  get isClickTroughPage(): boolean {
    return this.type === NavigationItemType.ClickThrough;
  }

  get directlyNavigableChildren(): UserNavigationItem[] {
    return this.children.filter(c => c.isDirectlyNavigable);
  }

  get hasNoDirectlyNavigableChildren(): boolean {
    return this.directlyNavigableChildren.length === 0;
  }

  get isMenuItemWithNoDirectlyNavigableDescendants(): boolean {
    return this.isMenu && !this.hasDirectlyNavigableDescendants;
  }

  get hasNoneClickTroughChildren(): boolean {
    return this.children.some(c => !c.isClickTroughPage);
  }

  get directlyNavigableDescendants(): UserNavigationItem[] {
    return this.asArray.filter(c => c.isDirectlyNavigable && !c.is(this));
  }

  get hasDirectlyNavigableDescendants(): boolean {
    return this.directlyNavigableDescendants.length > 0;
  }

  get hasExactlyOneNavigableDescendant(): boolean {
    return this.directlyNavigableDescendants.length === 1;
  }

  get firstNavigableDescendant(): UserNavigationItem {
    return UserNavigationItem.findFirstNavigableDescendantRecursive(this);
  }

  get opensInExternalTab(): boolean {
    return this.config?.openInExternalTab ?? false;
  }

  get externalUrl(): boolean {
    return this.config?.externalUrl ?? '';
  }

  get breadcrumbConfigValues(): string[] {
    if (!this.config || !this.egiPage?.config.configurationOptions) {
      return [];
    }

    const configOptionKeys = Object.keys(this.egiPage.config.configurationOptions);

    const valuesToShow = configOptionKeys.filter(k => {
      const configOption = this.egiPage.config.configurationOptions[k];
      return configOption.showValueInBreadCrumb;
    });

    return Object.keys(this.config)
      .filter(k => valuesToShow.includes(k))
      .map(k => `${this.config[k]}`);
  }

  constructor(
    id: string,
    meta: Partial<DataHistory>,
    appId: string,
    name: string,
    order: number,
    pageComponentId: string,
    parentId: string,
    type: NavigationItemType,
    children: UserNavigationItem[],
    public readonly isUserGroupScopeSet: boolean,
    public readonly isBaseAccessAllowed: boolean,
    public readonly allowedFeaturesForUser: string[],
    config: object | undefined
  ) {
    super(id, meta, appId, name, order, children, pageComponentId, type, undefined, name, config);
  }

  is(model?: NavigationItemData): boolean {
    return super.is(model);
  }

  static findFirstNavigableDescendantRecursive(
    item: UserNavigationItem
  ): UserNavigationItem | undefined {
    for (const c of item.directlyNavigableDescendants) {
      if (c.isDirectlyNavigable) {
        return c;
      }

      if (c.hasChildren) {
        return this.findFirstNavigableDescendantRecursive(c);
      }
    }

    return undefined;
  }

  static nonDirectlyNavigableTypes = [NavigationItemType.Menu, NavigationItemType.ClickThrough];

  static createDefault(type: NavigationItemType, pageId = ''): UserNavigationItem {
    return new UserNavigationItem(
      '',
      {},
      '',
      '',
      0,
      pageId,
      '',
      type,
      [],
      true,
      true,
      [],
      undefined
    );
  }

  static createForTests(config?: Partial<UserNavigationItem>) {
    return new UserNavigationItem(
      config?.id ?? '',
      {},
      config?.appId ?? '',
      config?.name ?? '',
      config?.order ?? 0,
      config.pageComponentId ?? '',
      config?.parentId ?? '',
      config?.type ?? NavigationItemType.Unknown,
      config?.children ?? [],
      config?.isUserGroupScopeSet ?? false,
      config?.isBaseAccessAllowed ?? false,
      config?.allowedFeaturesForUser ?? [],
      config?.config ?? undefined
    );
  }
}
